import React from "react";
import { CircularProgress, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const tooltips = {
  dimension:
    "Choose the data dimension that will be monitored for changes (e.g., completeness, uniqueness).",
  check: "Select the type of check to apply (e.g., anomaly detection, range validation).",
  ruleName: "Enter a brief description explaining the purpose and function of the rule.",
  description: "Specify the acceptable values for the selected column to maintain data consistency.",
  arguments: "Enter JSON arguments for the rule. Must be valid JSON format.",
  threshold: "Set the threshold value for the rule validation.",
  thresholdType: "Select the type of threshold (percentage, count, etc.).",
  sqlQuery: "Enter SQL query for SQL-based checks.",
  functionName:
    'Enter the function name for the rule. This field is only enabled when "is_locally_defined" check is selected.',
  ruleOwner: "Enter the email id of the rule owner (optional).",
};

const AssetRuleFormContent = ({
  formData,
  availableDimensions,
  availableChecks,
  hierarchyFormData,
  ruleNameCheckMessage,
  ruleNameIsUnique,
  ruleNameChecking,
  isRuleOwnerEmailValid,
  loading,
  editRuleData,
  onInputChange,
  onHierarchyChange,
  onClose,
  isSqlQueryEnabled,
  isFunctionNameEnabled,
  KeywordInputComponent,
  setFormData,
  error,
}) => {
  const KeywordInput = KeywordInputComponent;
  const hierarchyKeys = Object.keys(hierarchyFormData || {});

  return (
    <>
      <div className="mb-4 bg-blue-50 p-6 rounded-lg border border-blue-100">
        <h3 className="text-lg font-semibold text-[#bb1ccc] mb-4 pb-2 flex items-center">
          <div className="w-[4px] h-6 bg-blue-500 rounded mr-3"></div>
          DQ Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Dimension
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.dimension}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <select
              value={formData.dimension}
              onChange={(e) => onInputChange("dimension", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${
                formData.isConsistencyRule
                  ? "bg-[#e0e0e0] border border-gray-300 cursor-not-allowed"
                  : "border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              }`}
              required
              disabled={formData.isConsistencyRule}
            >
              <option value="">Select Dimension</option>
              {availableDimensions.map((dim) => (
                <option key={dim.id} value={dim.name}>
                  {dim.name.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase())}
                </option>
              ))}
            </select>
            {formData.isConsistencyRule && (
              <p className="text-xs text-gray-500 mt-1">Consistency rules cannot be modified</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Check
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.check}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <select
              value={formData.check}
              onChange={(e) => onInputChange("check", e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${
                !formData.dimension || formData.isConsistencyRule
                  ? "bg-[#e0e0e0] border border-gray-300 cursor-not-allowed"
                  : "border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              }`}
              required
              disabled={!formData.dimension || formData.isConsistencyRule}
            >
              <option value="">Select Check</option>
              {availableChecks.map((check) => (
                <option key={check.check_id} value={check.check_name}>
                  {check.check_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Rule Name
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.ruleName}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.ruleName}
                onChange={(e) => onInputChange("ruleName", e.target.value)}
                placeholder="Rule Name"
                className="w-full px-3 py-2 border border-[#bb1ccc] rounded-lg focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
                required
              />
              {ruleNameChecking && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <CircularProgress size={16} />
                </div>
              )}
            </div>
            {ruleNameCheckMessage && (
              <div
                className={`text-xs mt-1 ${
                  ruleNameIsUnique === false
                    ? "text-red-500"
                    : ruleNameIsUnique === true
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {ruleNameCheckMessage}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Rule Description
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.description}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => onInputChange("description", e.target.value)}
              placeholder="Rule Description"
              className={`w-full px-3 py-2 rounded-lg ${
                !formData.check
                  ? "bg-[#e0e0e0] border border-gray-300"
                  : "border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              }`}
              required
              disabled={!formData.check}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Keywords(Retailer name)</label>
            <KeywordInput field={{ name: "keywords" }} formData={formData} setFormData={setFormData} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Arguments
              <Tooltip
                title={tooltips.arguments}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <textarea
              value={formData.arguments}
              onChange={(e) => onInputChange("arguments", e.target.value)}
              placeholder="Enter JSON arguments..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              SQL Query
              <Tooltip
                title={tooltips.sqlQuery}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <textarea
              value={formData.sqlQuery}
              onChange={(e) => onInputChange("sqlQuery", e.target.value)}
              placeholder="Enter SQL Query..."
              rows={3}
              className={`w-full px-3 py-2 rounded-lg ${
                !isSqlQueryEnabled()
                  ? "bg-[#e0e0e0] border border-gray-300 cursor-not-allowed"
                  : "border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              }`}
              disabled={!isSqlQueryEnabled()}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Function Name
              <Tooltip
                title={tooltips.functionName}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <input
              type="text"
              value={formData.functionName}
              onChange={(e) => onInputChange("functionName", e.target.value)}
              placeholder="Enter Function Name"
              className={`w-full px-3 py-2 rounded-lg ${
                !isFunctionNameEnabled()
                  ? "bg-[#e0e0e0] border border-gray-300 cursor-not-allowed"
                  : "border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              }`}
              disabled={!isFunctionNameEnabled()}
            />
          </div>
        </div>
      </div>

      <div className="mb-4 bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-[#bb1ccc] mb-4 pb-2 flex items-center">
          <div className="w-[4px] h-6 bg-green-500 rounded mr-3"></div>
          Additional Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Threshold Type
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.thresholdType}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <select
              value={formData.thresholdType}
              onChange={(e) => onInputChange("thresholdType", e.target.value)}
              className="w-full px-3 py-2 border border-[#bb1ccc] rounded-lg focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              required
            >
              <option value="">Select Threshold Type</option>
              <option value="percentage">Percentage</option>
              <option value="count">Count</option>
              <option value="absolute">Absolute</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Threshold
              <span className="text-red-500">*</span>
              <Tooltip
                title={tooltips.threshold}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <input
              type="text"
              value={formData.threshold}
              onChange={(e) => onInputChange("threshold", e.target.value)}
              placeholder="Enter Threshold Value"
              className="w-full px-3 py-2 border border-[#bb1ccc] rounded-lg focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Rule Owner
              <Tooltip
                title={tooltips.ruleOwner}
                placement="top"
                arrow
                PopperProps={{
                  modifiers: [
                    {
                      name: "zIndex",
                      enabled: true,
                      phase: "write",
                      fn: ({ state }) => {
                        state.styles.popper.zIndex = 100000;
                      },
                    },
                  ],
                }}
                sx={{
                  "& .MuiTooltip-tooltip": {
                    fontSize: "18px !important",
                    backgroundColor: "#111 !important",
                    color: "#fff !important",
                  },
                }}
              >
                <InfoOutlinedIcon fontSize="small" sx={{ color: "#bb1ccc", cursor: "pointer" }} />
              </Tooltip>
            </label>
            <input
              type="text"
              value={formData.rule_owner}
              onChange={(e) => onInputChange("rule_owner", e.target.value)}
              placeholder="Rule owner (@effem.com)"
              className={`w-full px-3 py-2 rounded-lg border focus:ring-2 focus-visible:border-[#bb1ccc] ${
                formData.rule_owner && !isRuleOwnerEmailValid
                  ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                  : "border-[#bb1ccc] focus:ring-[#bb1ccc] focus:border-[#bb1ccc]"
              }`}
            />
            {formData.rule_owner && !isRuleOwnerEmailValid && (
              <div className="text-red-500 text-xs mt-1">Email must be from: effem.com</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Rule Phase
            </label>
            <select
              value={formData.rule_phase}
              onChange={(e) => onInputChange("rule_phase", e.target.value)}
              className="w-full px-3 py-2 border border-[#bb1ccc] rounded-lg focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
            >
              <option value="">Select Rule Phase</option>
              <option value="Pre - Refresh">Pre-Refresh</option>
              <option value="Post - Refresh">Post-Refresh</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Remediation Action</label>
            <textarea
              value={formData.remediation_action}
              rows={3}
              onChange={(e) => onInputChange("remediation_action", e.target.value)}
              placeholder="Remediation Action"
              className="w-full px-3 py-2 rounded-lg border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter</label>
            <textarea
              value={formData.filter}
              rows={3}
              onChange={(e) => onInputChange("filter", e.target.value)}
              placeholder="Filter"
              className="w-full px-3 py-2 rounded-lg border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
            />
          </div>
        </div>
        <div className="mt-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
              Rule Selection Key
            </label>
            <input
              type="text"
              value={formData.rule_selection_key || ""}
              onChange={(e) => onInputChange("rule_selection_key", e.target.value)}
              placeholder="Rule Selection Key"
              className="w-full px-3 py-2 rounded-lg border border-[#bb1ccc] focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
            />
          </div>
        </div>
      </div>

      <div className="mb-4 bg-purple-50 p-6 rounded-lg border border-purple-100">
        <h3 className="text-lg font-semibold text-[#bb1ccc] mb-4 pb-2 flex items-center">
          <div className="w-[4px] h-6 bg-purple-500 rounded mr-3"></div>
          Hierarchy Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {hierarchyKeys.map((key) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                {key}
              </label>
              <input
                type="text"
                name={key}
                value={hierarchyFormData[key] || ""}
                onChange={onHierarchyChange}
                placeholder={`Enter ${key}`}
                className="w-full px-3 py-2 border border-[#bb1ccc] rounded-lg focus:ring-2 focus:ring-[#bb1ccc] focus:border-[#bb1ccc] focus-visible:border-[#bb1ccc]"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-4 mt-8 pt-6 ">
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-2 text-white rounded-lg hover:bg-[#bb1ccc] transition-colors duration-200 bg-[#bb1ccc]"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`px-6 py-2 rounded-lg transition-colors duration-200 ${
            loading || (!editRuleData && ruleNameIsUnique === false) || ruleNameChecking
              ? "bg-gray-400 text-gray-600 cursor-not-allowed"
              : "bg-[#bb1ccc] text-white hover:bg-[#bb1ccc]"
          }`}
          disabled={loading || (!editRuleData && ruleNameIsUnique === false) || ruleNameChecking}
        >
          {loading ? "Saving..." : ruleNameChecking ? "Checking..." : "Save"}
        </button>
      </div>
      {error && <div className="text-red-600 mt-2">{error}</div>}
    </>
  );
};

export default AssetRuleFormContent;

